import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import type { OscarReceiptResponse, OscarSequenceReceipt } from '@/types/kalshi';

/**
 * #302 — loads the durable Oscar sequence receipt for one ticker.
 *
 * Two query shapes, one active at a time:
 *
 *   latest  — GET /api/trader/oscar-receipt?ticker=…
 *             the newest authorized sequence for this market. Runs on mount
 *             and on every ticker change, which is what makes a page refresh
 *             or a game reopen recover the receipt from durable audit data.
 *
 *   exact   — …&sequenceId=<uuid>
 *             the sequence id the client MINTED before a fire. Pinning the
 *             exact id matters: "latest for this ticker" could resolve to a
 *             different fire if two attempts interleave, and the whole point
 *             of the post-fire read is to show evidence for the attempt the
 *             trader just made.
 *
 * Staleness: every load takes a generation number, and a response is applied
 * only if its generation is still current. A ticker change, a new loadExact,
 * or a refresh all supersede whatever is in flight — so a slow response for
 * the market the trader just navigated away from can never paint over the one
 * they are looking at.
 *
 * The returned promises NEVER reject. `loadExact` is called from the fire
 * path's `finally`; a rejection there would surface as an unhandled rejection
 * and, worse, could interleave with the submission-reset semantics the fire
 * path owns. Read failures land in `error` and nowhere else.
 */

export interface UseOscarReceiptArgs {
  ticker: string | null;
  enabled: boolean;
}

export interface UseOscarReceiptResult {
  receipt: OscarSequenceReceipt | null;
  loading: boolean;
  error: string | null;
  loadLatest: () => Promise<void>;
  /** Load the exact minted sequence and make it the active query. */
  loadExact: (sequenceId: string) => Promise<void>;
  /** Re-run whichever query is active (latest, or the pinned sequence). */
  refresh: () => Promise<void>;
}

function receiptUrl(ticker: string, sequenceId: string | null): string {
  const params = new URLSearchParams({ ticker });
  if (sequenceId != null) params.set('sequenceId', sequenceId);
  return `/api/trader/oscar-receipt?${params.toString()}`;
}

export function useOscarReceipt({ ticker, enabled }: UseOscarReceiptArgs): UseOscarReceiptResult {
  const [receipt, setReceipt] = useState<OscarSequenceReceipt | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monotonic generation. Incremented by every load; a response whose
  // generation is stale is dropped rather than applied.
  const generationRef = useRef(0);
  // The sequence the active query is pinned to (null = latest for the ticker).
  const activeSequenceRef = useRef<string | null>(null);

  const load = useCallback(
    async (sequenceId: string | null): Promise<void> => {
      if (!enabled || !ticker) return;
      const generation = (generationRef.current += 1);
      setLoading(true);
      try {
        const res = await apiFetch(receiptUrl(ticker, sequenceId));
        if (generation !== generationRef.current) return;
        if (!res.ok) {
          setReceipt(null);
          setError(`Could not load the sequence receipt (HTTP ${res.status})`);
          return;
        }
        const body = (await res.json()) as OscarReceiptResponse;
        if (generation !== generationRef.current) return;
        setReceipt(body?.receipt ?? null);
        setError(null);
      } catch (err) {
        if (generation !== generationRef.current) return;
        // A failed READ says nothing about what rests at the venue, so the
        // previous receipt is dropped rather than left to look current.
        setReceipt(null);
        setError(err instanceof Error ? err.message : 'Could not load the sequence receipt');
      } finally {
        if (generation === generationRef.current) setLoading(false);
      }
    },
    [enabled, ticker],
  );

  const loadLatest = useCallback(async () => {
    activeSequenceRef.current = null;
    await load(null);
  }, [load]);

  const loadExact = useCallback(
    async (sequenceId: string) => {
      activeSequenceRef.current = sequenceId;
      await load(sequenceId);
    },
    [load],
  );

  const refresh = useCallback(async () => load(activeSequenceRef.current), [load]);

  useEffect(() => {
    if (!enabled || !ticker) {
      // Supersede anything in flight so a late response cannot paint a
      // receipt onto a ticket that no longer targets a Kalshi market.
      generationRef.current += 1;
      setReceipt(null);
      setError(null);
      setLoading(false);
      return;
    }
    // A new market means a new sequence; drop any pinned id from the old one.
    activeSequenceRef.current = null;
    void load(null);
  }, [ticker, enabled, load]);

  return { receipt, loading, error, loadLatest, loadExact, refresh };
}
